import { memo } from 'react';
import { getUser } from '@frontend/auth-helpers';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Avatar, Text, Truncated, useTooltip } from '@frontend/design-system';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { CardBody, DurationComponent, SelectableCard, DraggableCard, LocationChip } from './atoms';
import { TourHighlight } from './tour-highlight';
import { UserCardProps, UserSelectableCardProps, CardProps } from './types';
import { getSearchedNameParts } from './utils';

export const UserNameplate = ({
  name = i18next.t('Unknown'),
  subtitle = i18next.t('User'),
  searchTerm,
}: {
  name: string;
  subtitle?: string;
  searchTerm?: string;
}) => {
  let _name = (
    <Truncated
      as={Text}
      weight='bold'
      TooltipComponent={
        <Text size='medium' color='white'>
          {name}
        </Text>
      }
    >
      {name}
    </Truncated>
  );
  let _subtitle = (
    <Truncated as={Text} color='subdued' size='medium'>
      {subtitle}
    </Truncated>
  );

  if (searchTerm) {
    if (name.toLowerCase().includes(searchTerm.toLowerCase())) {
      const nameParts = getSearchedNameParts(name, searchTerm);

      _name = (
        <Truncated
          as={Text}
          weight='bold'
          TooltipComponent={
            <Text size='medium' color='white'>
              {name}
            </Text>
          }
        >
          {nameParts[0]}
          <Text
            as='span'
            weight='bold'
            css={{
              backgroundColor: theme.colors.primary50,
              color: theme.colors.white,
              borderRadius: theme.borderRadius.small,
            }}
          >
            {nameParts[1]}
          </Text>
          {nameParts[2]}
        </Truncated>
      );
    }

    if (subtitle.toLowerCase().includes(searchTerm.toLowerCase())) {
      const subtitleParts = getSearchedNameParts(subtitle, searchTerm);

      _subtitle = (
        <Truncated as={Text} color='subdued' size='medium'>
          {subtitleParts[0]}
          <Text
            as='span'
            css={{
              backgroundColor: theme.colors.primary50,
              color: theme.colors.white,
              borderRadius: theme.borderRadius.small,
            }}
          >
            {subtitleParts[1]}
          </Text>
          {subtitleParts[2]}
        </Truncated>
      );
    }
  }

  return (
    <>
      {_name}
      {_subtitle}
    </>
  );
};

const AvatarDot = ({ isOnline }: { isOnline?: boolean }) => {
  const { t } = useTranslation('phone');

  return isOnline ? (
    <Avatar.Dot variant='success' hoverText={t('User is online')} />
  ) : (
    <Avatar.Dot variant='neutral' hoverText={t('User is offline')} />
  );
};

export const UserAvatar = ({
  id,
  name,
  showStatus,
  isOnline,
}: {
  id: string;
  name: string;
  showStatus?: boolean;
  isOnline?: boolean;
}) => {
  const { t } = useTranslation('calls');
  return (
    <TourHighlight
      dotId={`user-avatar-${id}`}
      message={t('Users that are logged into Weave will show a green circle at the bottom of their avatar.')}
      placement='top'
      dotPosition={{ top: 1, right: 1 }}
    >
      <Avatar
        css={{
          // This overrides the default Avatar.Dot position at the bottom instead
          '#avatar-dot>:first-of-type': {
            bottom: -4,
            top: 'unset',
          },
        }}
        name={name}
        disableHoverLabel
      >
        {showStatus && (
          <div
            onClick={() => {
              pendo.track(teamPresenceTrackingId({ context: 'user-card-avatar::status-dot::dead-end' }));
            }}
          >
            <AvatarDot isOnline={isOnline} />
          </div>
        )}
      </Avatar>
    </TourHighlight>
  );
};

export const UserCard = memo(function UserCard({
  id,
  name,
  subtitle,
  isOnline,
  locations,
  orgId,
  deviceState,
  deviceName,
  deviceActivity,
  deviceExtension,
  capabilities = {
    canCall: true,
    canMessage: true,
    canRemove: true,
  },
  searchTerm,
  showLocations,
  onMessageClick,
  onCallClick,
  onRemoveClick,
  onPinClick,
  showUnpinIcon,
}: Omit<UserCardProps, 'type'> & { showLocations?: boolean }) {
  const { t } = useTranslation('phone');
  const isWeaveUser = getUser()?.type === 'weave';
  const messageTooltip = () => {
    if (capabilities?.isSelf) {
      return t('Cannot message yourself');
    }

    if (!capabilities?.canMessage) {
      return t('A user must connect to this device before you can chat');
    }

    return t('Message {{name}}', { name });
  };
  const callTooltip = () => {
    if (isWeaveUser) {
      return t("Weave users can't make calls");
    }

    if (capabilities?.isSelf) {
      return t('Cannot call yourself');
    }

    if (!capabilities?.canCall) {
      return t("This user can't receive calls - no device connected");
    }

    return t('Call {{name}}', { name });
  };

  const actions: CardProps['actions'] = [
    ...(!!capabilities?.hasTeamChatAccess
      ? [
          {
            icon: 'message' as const,
            label: t('Message'),
            onClick: () => {
              onMessageClick?.(id, name, orgId);
            },
            disabled: !capabilities?.canMessage || !!capabilities?.isSelf,
            tooltip: messageTooltip(),
            trackingId: teamPresenceTrackingId({ context: 'user-card::message' }),
          },
        ]
      : []),
    {
      icon: 'phone' as const,
      label: t('Call'),
      onClick: () => {
        onCallClick?.(deviceExtension);
      },
      tooltip: callTooltip(),
      callout: t('Click here to call a User or Device (not available in the tour).'),
      disabled: !capabilities?.canCall || !!capabilities?.isSelf || isWeaveUser,
      trackingId: teamPresenceTrackingId({ context: 'user-card::call' }),
    },
    {
      icon: 'remove2' as const,
      label: t('Remove'),
      onClick: () => {
        onRemoveClick?.(id, name);
      },
      tooltip: t('Remove {{name}}', { name }),
      disabled: !capabilities?.canRemove,
      trackingId: teamPresenceTrackingId({ context: 'user-card::remove' }),
    },
    {
      icon: showUnpinIcon ? ('pin-filled' as const) : ('pin' as const),
      label: showUnpinIcon ? t('Unpin from Top') : t('Pin to Top'),
      onClick: () => {
        onPinClick?.(id, name);
      },
      disabled: false,
      tooltip: showUnpinIcon ? t('Unpin from Top') : t('Pin to Top'),
      callout: t('Click here to pin a user or device for quick visibility.'),
      trackingId: teamPresenceTrackingId({ context: showUnpinIcon ? 'user-card::unpin-icon' : 'user-card::pin-icon' }),
    },
  ];

  const card = (
    <DraggableCard
      id={id}
      nameplate={<UserNameplate name={name} searchTerm={searchTerm} subtitle={subtitle} />}
      avatar={<UserAvatar id={id} name={name} isOnline={isOnline} showStatus />}
      actions={actions}
      body={
        <CardBody
          cardId={id}
          deviceState={deviceState}
          locations={locations}
          showLocations={showLocations}
          tooltip={t('This shows the Phone Status of a monitored user.')}
        />
      }
      meta={<UserCardMeta id={id} deviceActivity={deviceActivity} deviceName={deviceName} />}
    />
  );

  return card;
});

const UserCardMeta = ({
  id,
  deviceActivity,
  deviceName,
}: {
  id: string;
  deviceActivity: UserCardProps['deviceActivity'];
  deviceName: string;
}) => {
  const { t } = useTranslation('phone');
  const hasConnectedDevice = deviceActivity?.state && deviceName;
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ trigger: 'hover' });
  return (
    <div
      css={{
        display: 'grid',
        rowGap: theme.spacing(1),
        gridTemplateAreas: '"time" "device"',
        gridTemplateRows: '1fr 1fr',
      }}
    >
      {hasConnectedDevice && <DurationComponent deviceActivity={deviceActivity} />}
      <div
        css={{
          gridArea: 'device',
          display: 'grid',
          gridTemplateColumns: `auto 1fr`,
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        {hasConnectedDevice ? (
          <TourHighlight
            dotId={`user-device-${id}`}
            message={t('This is the Device that this user is connected to.')}
            placement='top'
            containerCss={{ display: 'inline-flex' }}
            dotPosition={{ top: -2, right: -5 }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: `auto 1fr`,
                gap: theme.spacing(1),
                alignItems: 'center',
              }}
            >
              <div {...triggerProps}>
                <Icon name='cell-phone-small' color='light' />
              </div>
              <Truncated as={Text} size='medium'>
                {deviceName}
              </Truncated>
              <Tooltip {...tooltipProps}>{t('Connected Device')}</Tooltip>
            </div>
          </TourHighlight>
        ) : (
          <>
            <Icon name='cell-phone-small' color='light' />
            <Text as={Text} size='medium'>
              {t('NO DEVICE CONNECTED')}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};

export const SelectableUserCard = ({
  id,
  name,
  email,
  searchTerm,
  locations,
  onChange,
  isSelected,
  disabled,
  disabledTooltip,
  showLocations,
}: UserSelectableCardProps & {
  showLocations?: boolean;
  disabledTooltip?: string;
}) => {
  return (
    <SelectableCard
      avatar={<UserAvatar id={id} name={name} />}
      nameplate={<UserNameplate name={name} subtitle={email} searchTerm={searchTerm} />}
      meta={showLocations ? <LocationChip locations={locations} /> : null}
      onChange={onChange}
      isSelected={isSelected}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
    />
  );
};
